import { Observable } from 'rxjs';
import { Action } from 'redux';
import { ofType, StateObservable } from 'redux-observable';
import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { CREATE_SUBSCRIPTION } from '../actions';
import {
  CreateSubscription,
  CreateSubscriptionSuccess,
  CreateSubscriptionError,
} from '../action-creators';
import { subscriptionFlow } from '@webapp/sdk/stripe';
import AppState from '../../state';
import { NoOp } from '../../ui/action-creators';
import * as subscriptions from '@webapp/sdk/subscriptions';
import { NewSubscription } from '@services/subscription/types/new-subscription';
import {
  buildNewSubscription,
  getSubscriptionChange,
  isNewSubscription,
} from '../selectors';
import { PlanNames } from '@services/product/types/plan-names';
import { PaymentFrequency } from '@services/product/types/payment-frequency';

export const createSubscriptionEpic = (
  action$: Observable<Action>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType<Action, CreateSubscription>(CREATE_SUBSCRIPTION),
    withLatestFrom(state$),
    mergeMap(async ([action, state]) => {
      try {
        const isCurrentSubscriptionExpiring =
          state.subscriptionManager.summary?.artists
            .find((a) => a.id === action.clientId)
            ?.subscriptions.find((s) => s.id === action.currentSubscription?.id)
            ?.isExpiring || false;

        const change = getSubscriptionChange(
          action.currentSubscription,
          action.order.product.name.toLowerCase() as PlanNames,
          action.order.price.frequency as PaymentFrequency,
          isCurrentSubscriptionExpiring
        );

        if (change === 'IGNORE' && !action.currentSubscription?.ended_at) {
          return new NoOp();
        }

        const activeCoupon = state.products.coupons.find((c) => c.isActive);

        const planOrder = action.order;
        const isNew = isNewSubscription(action.currentSubscription);

        const newSubscription: NewSubscription = buildNewSubscription(
          action.clientId,
          action.subscriptionType,
          planOrder
        );

        if (action.currentSubscription?.paypalId) {
          await subscriptions.cancel(action.currentSubscription.clientId);
        }

        let subscription = await subscriptions.create(
          action.clientId,
          newSubscription
        );

        if (action.stripe && action.paymentMethod) {
          const data = await subscriptionFlow(
            action.stripe,
            action.paymentMethod,
            action.clientId,
            planOrder.price.externalId as string,
            subscription.id,
            action.customer,
            action.currentSubscription?.externalId || subscription.externalId,
            activeCoupon,
            isNew,
            action.trialDays
          );

          subscription = {
            ...subscription,
            externalId: data.subscription.id,
            isActive: data.subscription.status === 'active',
          };
        }

        return new CreateSubscriptionSuccess(
          subscription,
          isNew,
          action.subscriptionType,
          action.order,
          action.customer,
          change,
          action.currentSubscription,
          action.redirectType,
          action.id,
          action.clientId,
          action.redirectTo,
          action.order.product.product_key,
          action.coupon,
          action.tax
        );
      } catch (err) {
        let message = (err as Error).message;

        if ((err as any).error?.message) {
          message = (err as any).error.message;
        }

        return new CreateSubscriptionError(message);
      }
    })
  );
